import React, { useState, useEffect, useRef } from "react";
import { CellContext } from "@tanstack/react-table";
import { Interview } from "@/services/interviewService";
import EditableCell from "./EditableCell";
import { flexRender } from "@tanstack/react-table";

/**
 * A component that renders an editable cell for the interview table
 * Handles editing state, keyboard navigation, and data updates
 */
export const EditableCellRenderer = ({
  getValue,
  row,
  column,
  table,
}: CellContext<Interview, unknown>) => {
  const initialValue = getValue();
  const columnId = column.id;
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  // Reference to store the latest value for date field
  const latestValueRef = useRef(value);

  // Get the custom data from the table meta
  const meta = table.options.meta as any;
  const currentUserRole = meta?.currentUserRole || "viewer";

  // Check if this interview was recently synced (frontend-only)
  const isRecentlySync = meta?.recentlySyncedIds?.has(row.original.id) || false;

  // Determine if the user can edit (only owners and editors can edit)
  const canEdit = currentUserRole === "owner" || currentUserRole === "editor";

  // Store computed inline suggestion for angle/network fields
  const computeSuggestion = (
    currentInput: string | number | undefined
  ): { full: string; remainder: string } => {
    // Only apply to angle and network columns
    if (!(columnId === "angle" || columnId === "network"))
      return { full: "", remainder: "" };

    const inputStr = (currentInput ?? "").toString();
    if (inputStr.trim() === "") return { full: "", remainder: "" };

    const lowerInput = inputStr.toLowerCase();

    // Gather unique values from this column across all rows
    const candidates: string[] = [];
    table.getRowModel().rows.forEach((r) => {
      const cellVal = (r.original as Interview)[columnId];
      if (typeof cellVal === "string" && cellVal.trim() !== "") {
        const candidate = cellVal.trim();
        if (
          candidate.toLowerCase().startsWith(lowerInput) &&
          candidate.toLowerCase() !== lowerInput
        ) {
          candidates.push(candidate);
        }
      }
    });

    const uniqueCandidates = Array.from(new Set(candidates));
    // Return first candidate (alphabetically sorted for consistency)
    uniqueCandidates.sort();
    const best = uniqueCandidates[0] ?? "";

    if (!best) return { full: "", remainder: "" };

    return { full: best, remainder: best.slice(inputStr.length) };
  };

  // Re-compute suggestion whenever value changes
  const { full: fullSuggestion, remainder: inlineSuggestion } =
    computeSuggestion(value as any);

  // Initialize state when the value from props changes
  useEffect(() => {
    setValue(initialValue);
    latestValueRef.current = initialValue;
  }, [initialValue]);

  // Register this cell with the table metadata when it mounts
  useEffect(() => {
    if (cellRef.current) {
      // Store cell ref in a data attribute for identification
      cellRef.current.dataset.cellId = `${row.original.id}:${columnId}`;
    }
  }, [row.original.id, columnId]);

  // Handler when editing cell
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle value change when editing
  const onValueChange = (newValue: string) => {
    // Store in ref immediately to ensure latest value is available for blur handlers
    latestValueRef.current = newValue;

    setValue(newValue);

    // For date and status fields, update immediately so display is in sync
    if (columnId === "date" || columnId === "status") {
      if (meta?.updateData) {
        meta.updateData(row.original.id, columnId as keyof Interview, newValue);
      }
    }
  };

  // Handle cell click to enter edit mode
  const handleCellClick = () => {
    if (columnId === "select") return; // Don't edit select column

    // Check if user has permission to edit
    if (!canEdit) {
      return; // Viewers cannot edit
    }

    setIsEditing(true);

    // Notify meta about the editing cell
    if (meta?.setEditingCell) {
      meta.setEditingCell({
        id: row.original.id,
        field: columnId as keyof Interview,
      });
    }
  };

  // Handle key presses during editing
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Determine the value that should ultimately be saved if user finishes editing now
    const typedStr = (value ?? "").toString();
    const acceptedValue =
      fullSuggestion && inlineSuggestion.length > 0 ? fullSuggestion : typedStr;

    if (e.key === "Enter") {
      // Save the current edit (accept suggestion, if any)
      if (meta?.updateData) {
        meta.updateData(
          row.original.id,
          columnId as keyof Interview,
          acceptedValue
        );
      }

      // Update local state to accepted value
      setValue(acceptedValue);

      // Prevent default Enter behavior to avoid form submission
      e.preventDefault();

      // Get all visible rows
      const rows = table.getRowModel().rows;
      const currentRowIndex = rows.findIndex((r) => r.id === row.id);
      const nextRowIndex = currentRowIndex + 1;

      // Exit edit mode
      setIsEditing(false);

      // If there's a next row, focus the same column in the next row
      if (nextRowIndex < rows.length) {
        const nextRowId = rows[nextRowIndex].original.id;

        setTimeout(() => {
          // Use document query to find the cell directly in the DOM
          const targetCell = document.querySelector(
            `[data-cell-id="${nextRowId}:${columnId}"]`
          );
          if (targetCell) {
            (targetCell as HTMLElement).click();
          }
        }, 50);
      }
    } else if (e.key === "Escape") {
      // Cancel the edit
      setValue(initialValue);
      setIsEditing(false);
    } else if (e.key === "Tab") {
      // Prevent default tab behavior
      e.preventDefault();

      // Save the current edit (accept suggestion if present)
      if (meta?.updateData) {
        meta.updateData(
          row.original.id,
          columnId as keyof Interview,
          acceptedValue
        );
      }

      // Update local value
      setValue(acceptedValue);

      // Exit edit mode
      setIsEditing(false);

      // Get all visible columns that can be edited (excluding the select column and non-editable columns)
      const visibleColumns = table
        .getVisibleLeafColumns()
        .filter((col) => col.id !== "select" && col.id !== "totalCost");

      // Find the index of the current column
      const currentColIndex = visibleColumns.findIndex(
        (col) => col.id === columnId
      );

      // Determine the next column based on shift key
      const nextColIndex = e.shiftKey
        ? currentColIndex - 1
        : currentColIndex + 1;

      // Target information for next cell
      let targetRowId = row.original.id;
      let targetColId = "";

      // Handle going to the next row if we're at the end of columns
      if (nextColIndex >= visibleColumns.length && !e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const nextRowIndex = currentRowIndex + 1;

        if (nextRowIndex < rows.length) {
          // Go to first column of next row
          targetRowId = rows[nextRowIndex].original.id;
          targetColId = visibleColumns[0].id;
        } else {
          // Stay in the current cell if we're at the last cell of the table
          targetRowId = row.original.id;
          targetColId = columnId;
        }
      }
      // Handle going to previous row if we're at the beginning of columns
      else if (nextColIndex < 0 && e.shiftKey) {
        const rows = table.getRowModel().rows;
        const currentRowIndex = rows.findIndex((r) => r.id === row.id);
        const prevRowIndex = currentRowIndex - 1;

        if (prevRowIndex >= 0) {
          // Go to last column of previous row
          targetRowId = rows[prevRowIndex].original.id;
          targetColId = visibleColumns[visibleColumns.length - 1].id;
        } else {
          // Stay in the current cell if we're at the first cell of the table
          targetRowId = row.original.id;
          targetColId = columnId;
        }
      }
      // Standard next/prev column in same row
      else if (nextColIndex >= 0 && nextColIndex < visibleColumns.length) {
        targetColId = visibleColumns[nextColIndex].id;
      } else {
        // No valid next cell (this should not happen with the improved logic)
        targetRowId = row.original.id;
        targetColId = columnId;
      }

      // Use setTimeout to ensure the current edit is completely finished
      setTimeout(() => {
        const targetCell = document.querySelector(
          `[data-cell-id="${targetRowId}:${targetColId}"]`
        );
        if (targetCell) {
          (targetCell as HTMLElement).click();
        }
      }, 50);
    }
  };

  // Handle blur when focus leaves the input
  const handleBlur = (id: string, field: keyof Interview) => {
    const typedStrBlur = (value ?? "").toString();
    const finalVal =
      fullSuggestion && inlineSuggestion.length > 0
        ? fullSuggestion
        : typedStrBlur;

    if (field === "date" || field === "status") {
      // Get the latest value from our ref, this should have been updated by onValueChange
      const latestValue = latestValueRef.current;

      // Update our local state to match (though we're about to exit editing mode)
      setValue(latestValue);

      // Directly update the data without delay for date and status fields
      if (meta?.updateData) {
        meta.updateData(id, field, latestValue);
      }

      setIsEditing(false);
    } else {
      setTimeout(() => {
        if (meta?.updateData) {
          meta.updateData(
            row.original.id,
            columnId as keyof Interview,
            finalVal
          );
        }
        setIsEditing(false);
      }, 100);
    }
  };

  // Return the editable cell component only for non-select columns
  if (columnId === "select") {
    return flexRender(column.columnDef.cell, { row, column, table } as any);
  }

  return (
    <div
      ref={cellRef}
      data-cell-id={`${row.original.id}:${columnId}`}
      onClick={!isEditing ? handleCellClick : undefined}
      className={`h-full ${canEdit ? "cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1">
        {/* Show sync indicator for first name column when recently synced */}
        {columnId === "expertFirstName" && isRecentlySync && (
          <div title="Recently synced from calendar">
            <div className="h-2 w-2 bg-green-600 rounded-full"></div>
          </div>
        )}

        <div className="flex-1">
          {isEditing ? (
            <EditableCell
              interview={{ ...row.original, [columnId]: value }}
              field={columnId as keyof Interview}
              isEditing={true}
              handleInputChange={(id, field, value) => onValueChange(value)}
              handleKeyDown={handleKeyDown}
              handleBlur={(id, field) => handleBlur(id, field)}
              inputRef={inputRef}
              inlineSuggestion={inlineSuggestion}
            />
          ) : (
            <EditableCell
              interview={row.original}
              field={columnId as keyof Interview}
              isEditing={false}
              handleInputChange={() => {}}
              handleKeyDown={() => {}}
              handleBlur={(id, field) => handleBlur(id, field)}
              inputRef={inputRef}
              inlineSuggestion={inlineSuggestion}
            />
          )}
        </div>
      </div>
    </div>
  );
};
